import CustomSlider from "@features/session-config/components/CustomSlider";
import { isWeb } from "@helpers/platform";
import { useAppTheme } from "@hooks/useAppTheme";
import type {
	Choice,
	SliderConfig,
} from "@memorang/types/src/session/new-session";
import { Box, TooltipWrapper } from "@memorang/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import { Animated, Pressable, View } from "react-native";
import { Caption, Icon, Text } from "react-native-paper";

interface Props {
	choices: Choice[];
	sliderConfig: SliderConfig;
	isAnswered?: boolean;
	itemId: number;
	onAnswer: (value: number) => void;
	storedValue?: number;
}

const ChoiceLabel = ({
	choice,
	align,
}: {
	choice: Choice;
	align: "left" | "right";
}) => {
	const theme = useAppTheme();
	const InfoButton = choice.infoTooltip ? (
		<TooltipWrapper title={choice.infoTooltip}>
			<Icon
				source="information-outline"
				size={18}
				color={theme.colors.primary}
			/>
		</TooltipWrapper>
	) : null;
	return (
		<Box
			flexDirection="row"
			alignItems="center"
			gap={6}
			style={{ flexShrink: 1 }}
		>
			{align === "left" ? (
				<>
					<Text
						variant="titleSmall"
						style={{
							color: theme.colors.onSurface,
							fontWeight: "600",
							letterSpacing: 0.15,
						}}
					>
						{choice.text || choice.stem}
					</Text>
					{InfoButton}
				</>
			) : (
				<>
					{InfoButton}
					<Text
						variant="titleSmall"
						style={{
							color: theme.colors.onSurface,
							fontWeight: "600",
							letterSpacing: 0.15,
						}}
					>
						{choice.text || choice.stem}
					</Text>
				</>
			)}
		</Box>
	);
};

const SliderChoice = ({
	choices,
	sliderConfig,
	isAnswered,
	itemId,
	onAnswer,
	storedValue,
}: Props) => {
	const theme = useAppTheme();
	const { snapPoints, suggestedValue, leftChoiceId, rightChoiceId } =
		sliderConfig;

	const sortedSnaps = useMemo(
		() => [...snapPoints].sort((a, b) => a.value - b.value),
		[snapPoints],
	);

	const minValue = sortedSnaps[0]?.value ?? 0;
	const maxValue = sortedSnaps[sortedSnaps.length - 1]?.value ?? 100;
	const _range = maxValue - minValue || 1;

	const midpoint = (minValue + maxValue) / 2;
	const [value, setValue] = useState<number>(storedValue ?? midpoint);
	const [hasInteracted, setHasInteracted] = useState(storedValue != null);

	const correctChoice = choices.find((c) => c.isCorrect);
	const correctSide: "left" | "right" | null = correctChoice
		? correctChoice.id === leftChoiceId
			? "left"
			: correctChoice.id === rightChoiceId
				? "right"
				: null
		: null;
	const correctSideLabel =
		correctSide === "left"
			? leftChoiceId
				? (choices.find((c) => c.id === leftChoiceId)?.text ?? "")
				: ""
			: correctSide === "right"
				? rightChoiceId
					? (choices.find((c) => c.id === rightChoiceId)?.text ?? "")
					: ""
				: "";

	const userSide: "left" | "right" = value <= midpoint ? "left" : "right";

	const matchesSide = isAnswered && correctSide && userSide === correctSide;

	const glowProgress = useRef(new Animated.Value(0)).current;

	useEffect(() => {
		if (!(isAnswered && correctSide)) {
			return;
		}
		const loop = Animated.loop(
			Animated.sequence([
				Animated.timing(glowProgress, {
					toValue: 1,
					duration: 1300,
					useNativeDriver: false,
				}),
				Animated.timing(glowProgress, {
					toValue: 0,
					duration: 1300,
					useNativeDriver: false,
				}),
			]),
		);
		loop.start();
		return () => loop.stop();
	}, [isAnswered, correctSide, glowProgress]);

	const glowOpacity = glowProgress.interpolate({
		inputRange: [0, 1],
		outputRange: [0.2, 0.5],
	});
	const glowScaleY = glowProgress.interpolate({
		inputRange: [0, 1],
		outputRange: [0.85, 1.15],
	});

	const snapToNearest = (raw: number) => {
		let nearest = sortedSnaps[0].value;
		let bestDelta = Math.abs(raw - nearest);
		for (const snap of sortedSnaps) {
			const delta = Math.abs(raw - snap.value);
			if (delta < bestDelta) {
				nearest = snap.value;
				bestDelta = delta;
			}
		}
		return nearest;
	};

	const handleChange = (raw: number) => {
		const snapped = snapToNearest(raw);
		if (hasInteracted && snapped === value) {
			return;
		}
		setValue(snapped);
		setHasInteracted(true);
		onAnswer(snapped);
	};

	const leftChoice = choices.find((c) => c.id === leftChoiceId) ?? choices[0];
	const rightChoice = choices.find((c) => c.id === rightChoiceId) ?? choices[1];

	const _currentSnap =
		sortedSnaps.find((s) => s.value === value) ?? sortedSnaps[0];

	const webMarks = sortedSnaps.map((snap) => ({
		value: snap.value,
		label: snap.label,
	}));

	return (
		<Box gap={20}>
			{isWeb ? (
				<Box gap={20}>
					<Box
						flexDirection="row"
						alignItems="center"
						justifyContent="space-between"
					>
						<ChoiceLabel choice={leftChoice} align="left" />
						<ChoiceLabel choice={rightChoice} align="right" />
					</Box>
					<Box
						alignSelf="center"
						width="88%"
						paddingHorizontal={12}
						paddingBottom={28}
						style={{ position: "relative" }}
					>
						{isAnswered && correctSide ? (
							<Animated.View
								pointerEvents="none"
								style={{
									position: "absolute",
									top: 6,
									height: 16,
									left:
										correctSide === "left" ? 8 : ("50%" as unknown as number),
									right:
										correctSide === "right" ? 8 : ("50%" as unknown as number),
									backgroundColor: theme.colors.primary,
									opacity: glowOpacity,
									borderRadius: 8,
									transform: [{ scaleY: glowScaleY }],
								}}
							/>
						) : null}
						<CustomSlider
							minValue={minValue}
							maxValue={maxValue}
							step={null}
							value={value}
							handleChange={handleChange}
							marks={webMarks}
							disabled={isAnswered}
							color={theme.colors.primary}
							testID={`slider-choice-${itemId}`}
						/>
					</Box>
				</Box>
			) : (
				<Box gap={8}>
					<Box
						flexDirection="row"
						alignItems="center"
						justifyContent="space-between"
					>
						<ChoiceLabel choice={leftChoice} align="left" />
						<ChoiceLabel choice={rightChoice} align="right" />
					</Box>
					<CustomSlider
						minValue={minValue}
						maxValue={maxValue}
						step={1}
						value={value}
						handleChange={handleChange}
						disabled={isAnswered}
						testID={`slider-choice-${itemId}`}
					/>
					<Box flexDirection="row" justifyContent="space-between">
						{sortedSnaps.map((snap) => {
							const isCurrent = snap.value === value;
							const snapSide: "left" | "right" =
								snap.value <= midpoint ? "left" : "right";
							const isOnCorrectSide =
								isAnswered && correctSide && snapSide === correctSide;
							return (
								<Pressable
									key={snap.value}
									onPress={
										isAnswered ? undefined : () => handleChange(snap.value)
									}
									disabled={isAnswered}
									style={{ alignItems: "center", maxWidth: 96, gap: 4 }}
								>
									<View
										style={{
											width: 10,
											height: 10,
											borderRadius: 5,
											backgroundColor: isCurrent
												? theme.colors.primary
												: isOnCorrectSide
													? theme.colors.primary
													: theme.colors.outlineVariant,
											opacity: isCurrent ? 1 : isOnCorrectSide ? 0.4 : 1,
										}}
									/>
									<Caption style={{ textAlign: "center" }}>
										{snap.label}
									</Caption>
								</Pressable>
							);
						})}
					</Box>
				</Box>
			)}

			{isAnswered && correctSide && correctSideLabel ? (
				<Box
					flexDirection="row"
					alignItems="center"
					gap={8}
					alignSelf="flex-start"
					paddingVertical={6}
					paddingHorizontal={12}
					borderRadius={20}
					backgroundColor={
						matchesSide
							? theme.colors.primaryContainer
							: theme.colors.surfaceVariant
					}
				>
					<Icon
						source={matchesSide ? "check-circle" : "lightbulb-on-outline"}
						size={16}
						color={theme.colors.primary}
					/>
					<Text variant="labelMedium" style={{ color: theme.colors.onSurface }}>
						{matchesSide
							? `Correct lean — ${correctSideLabel}`
							: `Suggested lean — ${correctSideLabel}`}
					</Text>
					<Text
						variant="labelSmall"
						style={{ color: theme.colors.onSurfaceVariant, marginLeft: 4 }}
					>
						(any intensity on this side counts)
					</Text>
				</Box>
			) : null}
		</Box>
	);
};

export default SliderChoice;
